import { apiPath } from "../../lib/apiPath";
import type { ChallengeAttempt, ChallengeLobbyTip, ChallengeStatusResponse } from "./types";

const jsonHeaders = { "Content-Type": "application/json", Accept: "application/json" };

function browserTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && tz.length > 0 ? tz : undefined;
  } catch {
    return undefined;
  }
}

export async function fetchChallengeStatus(): Promise<ChallengeStatusResponse | null> {
  const tz = browserTimeZone();
  const q = tz ? `?${new URLSearchParams({ timeZone: tz })}` : "";
  const r = await fetch(`${apiPath("/api/challenges")}${q}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`challenges ${r.status}`);
  return (await r.json()) as ChallengeStatusResponse;
}

export type ChallengeSpinModeResult = ChallengeStatusResponse & {
  resultIndex: number;
  segments: Array<{
    id: string;
    modeId: string;
    modeLabel: string;
    metric: string;
    goalValue: number;
    levelIndex?: number | null;
  }>;
  attempt: ChallengeAttempt;
};

export async function postChallengeSpinMode(): Promise<ChallengeSpinModeResult> {
  const r = await fetch(apiPath("/api/challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "spin-mode", timeZone: browserTimeZone() }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || `spin-mode ${r.status}`), { data, status: r.status });
  return data as ChallengeSpinModeResult;
}

export async function postChallengeFail(token?: string | null): Promise<ChallengeStatusResponse> {
  const r = await fetch(apiPath("/api/challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "fail", token: token || undefined, timeZone: browserTimeZone() }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || `fail ${r.status}`), { data, status: r.status });
  return data as ChallengeStatusResponse;
}

export type ChallengeClaimResult = ChallengeStatusResponse & {
  rewardAmount: number | null;
  rewardIndex: number;
  rewardSegments: number[];
  dotsBalance: number;
  alreadyClaimed?: boolean;
};

export async function postChallengeClaimReward(
  token: string,
  progress: { score: number; timeSec: number },
): Promise<ChallengeClaimResult> {
  const r = await fetch(apiPath("/api/challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      action: "claim-reward",
      token,
      progress,
      timeZone: browserTimeZone(),
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || `claim-reward ${r.status}`), { data, status: r.status });
  return data as ChallengeClaimResult;
}

export type AdminChallengeTemplate = {
  id: string;
  modeId: string;
  modeLabel: string;
  metric: string;
  goalValue: number;
  levelIndex?: number | null;
  active: boolean;
  sortOrder: number;
};

export type AdminChallengeRewardWeight = {
  amountDots: number;
  probabilityBasisPoints: number;
  probabilityPercent: number;
};

export type AdminChallengeLobbyTip = ChallengeLobbyTip & {
  active: boolean;
  sortOrder: number;
};

export async function fetchAdminChallenges(): Promise<{
  templates: AdminChallengeTemplate[];
  rewardWeights: AdminChallengeRewardWeight[];
  lobbyTips: AdminChallengeLobbyTip[];
}> {
  const r = await fetch(apiPath("/api/admin-challenges"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin-challenges ${r.status}`);
  return r.json();
}

export async function createAdminChallengeTemplate(body: {
  modeId: string;
  metric: string;
  goalValue: number;
  levelIndex?: number | null;
  active?: boolean;
  sortOrder?: number;
}): Promise<AdminChallengeTemplate> {
  const r = await fetch(apiPath("/api/admin-challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `create ${r.status}`);
  return data.template as AdminChallengeTemplate;
}

export async function updateAdminChallengeTemplate(body: {
  id: string;
  modeId?: string;
  metric?: string;
  goalValue?: number;
  levelIndex?: number | null;
  active?: boolean;
  sortOrder?: number;
}): Promise<AdminChallengeTemplate> {
  const r = await fetch(apiPath("/api/admin-challenges"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `update ${r.status}`);
  return data.template as AdminChallengeTemplate;
}

export async function deleteAdminChallengeTemplate(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-challenges")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`delete ${r.status}`);
}

export async function saveAdminChallengeRewardWeights(
  weights: Array<{ amountDots: number; probabilityPercent: number }>,
): Promise<AdminChallengeRewardWeight[]> {
  const r = await fetch(apiPath("/api/admin-challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "rewards", weights }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || data.error || `rewards ${r.status}`);
  return data.rewardWeights as AdminChallengeRewardWeight[];
}

export async function resetAdminDailyChallenges(opts?: {
  scope?: "all" | "users";
  userIds?: string[];
}): Promise<{ cleared: number; scanned: number; scope: "all" | "users"; requested: number }> {
  const scope = opts?.scope === "users" ? "users" : "all";
  const userIds = (opts?.userIds || []).map((id) => String(id || "").trim()).filter(Boolean);
  const r = await fetch(apiPath("/api/admin-challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      section: "reset-daily",
      scope,
      userIds: scope === "users" ? userIds : undefined,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `reset-daily ${r.status}`);
  return {
    cleared: Math.max(0, Math.floor(Number(data.cleared) || 0)),
    scanned: Math.max(0, Math.floor(Number(data.scanned) || 0)),
    scope: data.scope === "users" ? "users" : "all",
    requested: Math.max(0, Math.floor(Number(data.requested) || 0)),
  };
}

export async function createAdminChallengeLobbyTip(
  body: Omit<AdminChallengeLobbyTip, "id" | "sortOrder"> & { sortOrder?: number },
): Promise<AdminChallengeLobbyTip> {
  const r = await fetch(apiPath("/api/admin-challenges"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "tip", ...body }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `create tip ${r.status}`);
  return data.tip as AdminChallengeLobbyTip;
}

export async function updateAdminChallengeLobbyTip(
  body: Partial<AdminChallengeLobbyTip> & { id: string },
): Promise<AdminChallengeLobbyTip> {
  const r = await fetch(apiPath("/api/admin-challenges"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "tip", ...body }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `update tip ${r.status}`);
  return data.tip as AdminChallengeLobbyTip;
}

export async function deleteAdminChallengeLobbyTip(id: string): Promise<void> {
  const r = await fetch(
    `${apiPath("/api/admin-challenges")}?${new URLSearchParams({ id, section: "tip" })}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!r.ok) throw new Error(`delete tip ${r.status}`);
}
